import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { clearPublicSettingsCache } from "../../hooks/usePublicSettings";
import BookingFieldsSection from "../../components/admin/BookingFieldsSection";
import { Settings2, Save, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { saveSuccess, saveError } from "../../lib/saveBanner";

/**
 * Rental Settings — global rental-engine configuration that isn't part
 * of business finance policies. v3.8.5 adds the Driver Age Policy
 * section (moved out of the per-Car-Group Fleet editor) and drops the
 * Features/Extras catalogues (now hosted on /admin/car-extras).
 */
export default function RentalSettings() {
  return (
    <div className="max-w-6xl px-6 lg:px-10 pt-6 lg:pt-10 pb-12" data-testid="rental-settings-page">
      <div className="mb-8">
        <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">
          Rental
        </p>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2 flex items-center gap-3">
          <Settings2 size={28} /> Settings
        </h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-2xl">
          Configure the global brand, driver age policy, and the customer &
          admin checkout fields. Extras and Car Features live on their own
          page under <em>Car Extras</em>.
        </p>
      </div>

      <div className="space-y-6">
        <BrandSection />
        <DriverAgePolicySection />
        <section
          className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded-lg overflow-hidden"
          data-testid="booking-fields-panel"
        >
          <header className="px-6 py-4 flex items-center justify-between border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827]">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Section</p>
              <h2 className="font-display text-xl mt-1">Booking Fields</h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                Configure every field on the public checkout AND the admin manual booking form.
              </p>
            </div>
          </header>
          <div className="p-6">
            <BookingFieldsSection />
          </div>
        </section>
      </div>
    </div>
  );
}

// ── Company / Brand ─────────────────────────────────────────────────────────
function BrandSection() {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/settings")
      .then((r) => setName(r.data?.company_name || ""))
      .finally(() => setLoading(false));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { data: current } = await api.get("/admin/settings");
      const { data } = await api.put("/admin/settings", {
        ...current, company_name: name.trim(),
      });
      setName(data?.company_name || "");
      clearPublicSettingsCache();
      saveSuccess();
    } catch (err) {
      saveError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <section
      className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded-lg overflow-hidden"
      data-testid="brand-panel"
    >
      <header className="px-6 py-4 border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827]">
        <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Section</p>
        <h2 className="font-display text-xl mt-1">Company / Brand</h2>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
          Shown top-left in the admin console, in the public footer, and on rental agreements.
        </p>
      </header>
      <div className="p-6 flex flex-col md:flex-row gap-4 md:items-end">
        <label className="flex-1">
          <span className="block text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400 mb-2">
            Company / brand name
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Brainart Master Demo Rent a Car"
            disabled={loading || saving}
            data-testid="brand-name-input"
            className="w-full bg-transparent border border-black/15 dark:border-white/15 rounded px-3 py-2 text-sm focus:outline-none focus:border-black dark:focus:border-white"
          />
        </label>
        <button
          type="button"
          onClick={save}
          disabled={loading || saving}
          data-testid="brand-name-save"
          className="bg-black text-white dark:bg-white dark:text-black text-xs px-5 py-2.5 rounded inline-flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          Save brand
        </button>
      </div>
    </section>
  );
}

// ── Driver Age Policy (v3.8.5) ─────────────────────────────────────────────
const AGE_FIELDS = [
  ["min_driver_age",             "Min driver age",           { min: 21, max: 99, step: 1, testId: "dap-min-age" }],
  ["max_driver_age",             "Max driver age",           { min: 30, max: 99, step: 1, testId: "dap-max-age" }],
  ["young_driver_threshold",     "Young threshold (age <)",  { min: 21, max: 35, step: 1, testId: "dap-yds-threshold" }],
  ["senior_driver_threshold",    "Senior threshold (age >)", { min: 60, max: 75, step: 1, testId: "dap-sds-threshold" }],
  ["young_driver_fee_per_day",   "YDS fee (€ / day)",        { min: 0,  step: 0.01, testId: "dap-yds-fee" }],
  ["senior_driver_fee_per_day",  "SDS fee (€ / day)",        { min: 0,  step: 0.01, testId: "dap-sds-fee" }],
];

function DriverAgePolicySection() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/settings")
      .then((r) => {
        if (cancelled) return;
        setForm({
          min_driver_age:            r.data?.min_driver_age            ?? 21,
          max_driver_age:            r.data?.max_driver_age            ?? 75,
          young_driver_threshold:    r.data?.young_driver_threshold    ?? 25,
          senior_driver_threshold:   r.data?.senior_driver_threshold   ?? 72,
          young_driver_fee_per_day:  r.data?.young_driver_fee_per_day  ?? 0,
          senior_driver_fee_per_day: r.data?.senior_driver_fee_per_day ?? 0,
        });
      })
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || e.message))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const { data: current } = await api.get("/admin/settings");
      const payload = { ...current };
      for (const [k] of AGE_FIELDS) payload[k] = Number(form[k]) || 0;
      const { data } = await api.put("/admin/settings", payload);
      setForm({
        min_driver_age:            data?.min_driver_age            ?? form.min_driver_age,
        max_driver_age:            data?.max_driver_age            ?? form.max_driver_age,
        young_driver_threshold:    data?.young_driver_threshold    ?? form.young_driver_threshold,
        senior_driver_threshold:   data?.senior_driver_threshold   ?? form.senior_driver_threshold,
        young_driver_fee_per_day:  data?.young_driver_fee_per_day  ?? form.young_driver_fee_per_day,
        senior_driver_fee_per_day: data?.senior_driver_fee_per_day ?? form.senior_driver_fee_per_day,
      });
      clearPublicSettingsCache();
      saveSuccess();
    } catch (err) {
      saveError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded-lg overflow-hidden"
      data-testid="driver-age-policy-section"
    >
      <header className="px-6 py-4 border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827]">
        <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Policy</p>
        <h2 className="font-display text-xl mt-1 flex items-center gap-2">
          <ShieldAlert size={18} /> Driver Age Policy
        </h2>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 max-w-2xl">
          Global bounds and surcharges applied to every booking. Young Driver
          Surcharge (YDS) triggers when age &lt; young threshold. Senior (SDS)
          triggers when age &gt; senior threshold, up to max age. A Car Group
          can still override these values if it needs a stricter policy.
        </p>
      </header>
      <div className="p-6">
        {loading || !form ? (
          <p className="text-sm text-neutral-500">Loading policy…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-5">
              {AGE_FIELDS.map(([key, label, opts]) => (
                <label key={key} className="block">
                  <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 block mb-2">{label}</span>
                  <input
                    type="number"
                    min={opts.min}
                    max={opts.max}
                    step={opts.step}
                    value={form[key] ?? 0}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    data-testid={opts.testId}
                    className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-2 outline-none focus:border-black dark:focus:border-white text-sm tabular-nums"
                  />
                </label>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="dap-save"
                className="bg-black text-white dark:bg-white dark:text-black text-xs px-5 py-2.5 rounded inline-flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                Save policy
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
